import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { useContextMenu } from '../../contexts/ContextMenuContext';
import { useWindows } from '../../contexts/WindowContext';
import { useDesktopIcons, gridToPixel } from '../../contexts/DesktopIconsContext';
import { DesktopIcon } from './DesktopIcon';
import { DEFAULT_DESKTOP_ICONS, ICON_SIZE } from '../../utils/constants';
import type { ContextMenuItem } from '../../types';
import styles from './Desktop.module.css';

interface SelectionBox {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

export const Desktop: React.FC = () => {
  const { settings } = useSettings();
  const { showContextMenu } = useContextMenu();
  const { arrangeWindows } = useWindows();
  const { resetPositions, selectMultiple, clearSelection, getIconPosition } = useDesktopIcons();

  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const desktopRef = useRef<HTMLDivElement>(null);

  // Desktop context menu items
  const getDesktopMenuItems = useCallback((): ContextMenuItem[] => [
    {
      id: 'refresh',
      label: 'Refresh',
      icon: 'refresh',
      action: () => {
        document.body.style.cursor = 'wait';
        setTimeout(() => {
          document.body.style.cursor = 'default';
        }, 200);
      },
    },
    { id: 'divider-1', label: '', divider: true },
    {
      id: 'arrange-icons',
      label: 'Arrange Icons',
      submenu: [
        {
          id: 'auto-arrange',
          label: 'Auto Arrange',
          action: resetPositions,
        },
        {
          id: 'align-to-grid',
          label: 'Align to Grid',
          disabled: true,
        },
      ],
    },
    { id: 'divider-2', label: '', divider: true },
    {
      id: 'arrange-cascade',
      label: 'Cascade Windows',
      action: () => arrangeWindows('cascade'),
    },
    {
      id: 'arrange-tile-h',
      label: 'Tile Windows Horizontally',
      action: () => arrangeWindows('tile-horizontal'),
    },
    {
      id: 'arrange-tile-v',
      label: 'Tile Windows Vertically',
      action: () => arrangeWindows('tile-vertical'),
    },
    { id: 'divider-3', label: '', divider: true },
    {
      id: 'properties',
      label: 'Properties',
      icon: 'settings',
      action: () => {
        // Could open settings/display properties
      },
    },
  ], [arrangeWindows, resetPositions]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    showContextMenu(
      { x: e.clientX, y: e.clientY },
      getDesktopMenuItems(),
      'desktop'
    );
  }, [showContextMenu, getDesktopMenuItems]);

  // Check if a point is within an icon's bounds
  const isPointOnIcon = useCallback((x: number, y: number): boolean => {
    for (const icon of DEFAULT_DESKTOP_ICONS) {
      const gridPos = getIconPosition(icon.id);
      const pixelPos = gridToPixel(gridPos);
      const iconWidth = ICON_SIZE;
      const iconHeight = ICON_SIZE + 20; // Include label height

      if (
        x >= pixelPos.x &&
        x <= pixelPos.x + iconWidth &&
        y >= pixelPos.y &&
        y <= pixelPos.y + iconHeight
      ) {
        return true;
      }
    }
    return false;
  }, [getIconPosition]);

  // Get icons within selection box
  const getIconsInSelectionBox = useCallback((box: SelectionBox): string[] => {
    const left = Math.min(box.startX, box.currentX);
    const right = Math.max(box.startX, box.currentX);
    const top = Math.min(box.startY, box.currentY);
    const bottom = Math.max(box.startY, box.currentY);

    const selectedIds: string[] = [];

    for (const icon of DEFAULT_DESKTOP_ICONS) {
      const gridPos = getIconPosition(icon.id);
      const pixelPos = gridToPixel(gridPos);
      const iconWidth = ICON_SIZE;
      const iconHeight = ICON_SIZE + 20; // Include label

      // Check if icon intersects with selection box
      const iconLeft = pixelPos.x;
      const iconRight = pixelPos.x + iconWidth;
      const iconTop = pixelPos.y;
      const iconBottom = pixelPos.y + iconHeight;

      if (
        iconRight >= left &&
        iconLeft <= right &&
        iconBottom >= top &&
        iconTop <= bottom
      ) {
        selectedIds.push(icon.id);
      }
    }

    return selectedIds;
  }, [getIconPosition]);

  // Handle mouse down on desktop (start selection box)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only start selection on left click and if not clicking on an icon
    if (e.button !== 0) return;

    // Get click position relative to desktop
    const rect = desktopRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Don't start selection if clicking on an icon
    if (isPointOnIcon(x, y)) return;

    // Clear selection when clicking on empty desktop
    clearSelection();

    setSelectionBox({
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
    });
    setIsSelecting(true);
  }, [isPointOnIcon, clearSelection]);

  // Handle mouse move (update selection box)
  useEffect(() => {
    if (!isSelecting) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = desktopRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setSelectionBox(prev => {
        if (!prev) return null;
        return { ...prev, currentX: x, currentY: y };
      });
    };

    const handleMouseUp = () => {
      if (selectionBox) {
        const selectedIds = getIconsInSelectionBox(selectionBox);
        if (selectedIds.length > 0) {
          selectMultiple(selectedIds);
        }
      }
      setSelectionBox(null);
      setIsSelecting(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isSelecting, selectionBox, getIconsInSelectionBox, selectMultiple]);

  // Calculate selection box style
  const getSelectionBoxStyle = useCallback((): React.CSSProperties | null => {
    if (!selectionBox) return null;

    const left = Math.min(selectionBox.startX, selectionBox.currentX);
    const top = Math.min(selectionBox.startY, selectionBox.currentY);
    const width = Math.abs(selectionBox.currentX - selectionBox.startX);
    const height = Math.abs(selectionBox.currentY - selectionBox.startY);

    return {
      left,
      top,
      width,
      height,
    };
  }, [selectionBox]);

  const selectionBoxStyle = getSelectionBoxStyle();

  return (
    <div
      ref={desktopRef}
      className={styles.desktop}
      style={{
        backgroundImage: settings.wallpaper && settings.wallpaper !== 'none'
          ? `url(${settings.wallpaper})`
          : undefined,
        backgroundColor: settings.wallpaper === 'none' || !settings.wallpaper
          ? '#3A6EA5'
          : undefined,
      }}
      role="main"
      aria-label="Desktop"
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
    >
      <div className={styles.iconsContainer}>
        {DEFAULT_DESKTOP_ICONS.map((icon) => (
          <DesktopIcon
            key={icon.id}
            id={icon.id}
            name={icon.name}
            icon={icon.icon}
            appType={icon.appType}
            iconSize={icon.iconSize}
          />
        ))}
      </div>

      {/* Selection box (rubber band) */}
      {selectionBoxStyle && (
        <div
          className={styles.selectionBox}
          style={selectionBoxStyle}
        />
      )}
    </div>
  );
};

export default Desktop;
